import { useEffect, useMemo, useState } from "react";
import JSZip from "jszip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Upload, Settings, History, ExternalLink, FileArchive, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BENCHMARKS, FundHouse, Holding } from "./shared";

interface ExtractedHolding { holding_name: string; holding_weight_pct: number; }
interface SnapshotRow {
  id: string; snapshot_at: string; reason: string;
  source_filename: string | null; drive_file_url: string | null;
  holdings: ExtractedHolding[];
}

export default function FundHouseHoldings() {
  const [houses, setHouses] = useState<FundHouse[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [mappingFreshness, setMappingFreshness] = useState<Record<string, string | null>>({}); // holding_id -> latest mapping updated_at
  const [selectedName, setSelectedName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ name: string; value: string } | null>(null);

  // Factsheet upload state
  const [uploadBM, setUploadBM] = useState<number>(1);
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedHolding[] | null>(null);
  const [extractedMeta, setExtractedMeta] = useState<{ source_filename: string; drive_file_id: string | null; drive_file_url: string | null; as_of_date: string | null } | null>(null);
  const [applying, setApplying] = useState(false);

  // Batch (ZIP) upload state
  type BatchItem = {
    filename: string;
    benchmark: number;
    status: "pending" | "extracting" | "ready" | "error" | "applied" | "skipped";
    holdings: ExtractedHolding[];
    drive_file_id: string | null;
    drive_file_url: string | null;
    as_of_date: string | null;
    error?: string | null;
    approved: boolean;
  };
  const [batchItems, setBatchItems] = useState<BatchItem[] | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [batchApplying, setBatchApplying] = useState(false);

  // Settings (Drive folder)
  const [showSettings, setShowSettings] = useState(false);
  const [driveFolderId, setDriveFolderId] = useState("");
  const [driveFolderSaved, setDriveFolderSaved] = useState("");

  // Snapshot history
  const [showHistory, setShowHistory] = useState(false);
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);

  // Raw holdings-book total per fund_house_id, across every house (see loadCompleteness).
  const [bookSums, setBookSums] = useState<Map<string, number>>(new Map());

  const houseNames = useMemo(() => {
    const set = new Set<string>();
    houses.forEach(h => set.add(h.name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [houses]);

  const houseByBenchmark = useMemo(() => {
    const m = new Map<number, string>();
    houses.filter(h => h.name === selectedName).forEach(h => m.set(h.benchmark, h.id));
    return m;
  }, [houses, selectedName]);

  const loadHouses = async () => {
    const { data, error } = await supabase
      .from("bm_fund_houses").select("*").eq("is_active", true)
      .order("benchmark").order("sort_order").order("name");
    if (error) toast.error(error.message);
    setHouses((data || []) as FundHouse[]);
  };

  const loadHoldings = async () => {
    if (!selectedName) { setHoldings([]); setMappingFreshness({}); setLoading(false); return; }
    setLoading(true);
    const ids = Array.from(houseByBenchmark.values());
    if (!ids.length) { setHoldings([]); setMappingFreshness({}); setLoading(false); return; }
    const { data, error } = await supabase
      .from("bm_fund_house_holdings").select("*")
      .in("fund_house_id", ids)
      .order("holding_name");
    if (error) toast.error(error.message);
    const hldRows = (data || []) as Holding[];
    setHoldings(hldRows);

    // Load latest sector mapping updated_at per holding
    const hldIds = hldRows.map(h => h.id);
    if (hldIds.length) {
      const { data: mapData, error: mapErr } = await supabase
        .from("bm_holding_sector_mappings")
        .select("holding_id, updated_at")
        .in("holding_id", hldIds);
      if (mapErr) toast.error(mapErr.message);
      const fresh: Record<string, string | null> = {};
      hldIds.forEach(id => { fresh[id] = null; });
      (mapData || []).forEach((m: any) => {
        const cur = fresh[m.holding_id];
        if (!cur || new Date(m.updated_at) > new Date(cur)) fresh[m.holding_id] = m.updated_at;
      });
      setMappingFreshness(fresh);
    } else {
      setMappingFreshness({});
    }
    setLoading(false);
    // Every caller of loadHoldings() is either a fund-house switch or the tail of a mutation,
    // so refreshing here keeps the completeness banner from going stale after an edit.
    void loadCompleteness();
  };

  // Estate-wide completeness check.
  //
  // bm_recompute_allocations() normalises every fund house by its own holdings total
  // (holding_weight_pct / SUM(holding_weight_pct) * 100) before applying sector mappings, so a
  // house whose raw book sums to 65% or 170% still presents as a tidy 100% in bm_allocations.
  // That hides exactly the class of error where a factsheet route captured the wrong table or
  // dropped rows. This surfaces the raw sums for every house, not just the selected one.
  const loadCompleteness = async () => {
    // Paginate: bm_fund_house_holdings is already ~815 rows and PostgREST caps any single
    // response at 1000. A silent truncation here would under-report sums and invent failures.
    const PAGE = 500;
    const rows: Array<{ fund_house_id: string; holding_weight_pct: number }> = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from("bm_fund_house_holdings")
        .select("fund_house_id, holding_weight_pct")
        .order("id")
        .range(from, from + PAGE - 1);
      if (error) { toast.error(error.message); return; }
      const page = (data || []) as Array<{ fund_house_id: string; holding_weight_pct: number }>;
      rows.push(...page);
      if (page.length < PAGE) break;
    }
    const sums = new Map<string, number>();
    rows.forEach(r => sums.set(r.fund_house_id, (sums.get(r.fund_house_id) || 0) + (Number(r.holding_weight_pct) || 0)));
    setBookSums(sums);
  };

  const loadSettings = async () => {
    const { data } = await supabase.from("bm_factsheet_settings").select("drive_root_folder_id").eq("id", 1).maybeSingle();
    setDriveFolderId(data?.drive_root_folder_id || "");
    setDriveFolderSaved(data?.drive_root_folder_id || "");
  };

  const loadSnapshots = async () => {
    if (!selectedName) return;
    const ids = Array.from(houseByBenchmark.values());
    if (!ids.length) { setSnapshots([]); return; }
    const { data, error } = await supabase
      .from("bm_fund_house_holdings_snapshots")
      .select("id, snapshot_at, reason, source_filename, drive_file_url, holdings, fund_house_id")
      .in("fund_house_id", ids)
      .order("snapshot_at", { ascending: false })
      .limit(50);
    if (error) { toast.error(error.message); return; }
    setSnapshots((data || []) as any);
  };

  const saveDriveFolder = async () => {
    const value = driveFolderId.trim();
    const { error } = await supabase.from("bm_factsheet_settings").upsert({ id: 1, drive_root_folder_id: value || null });
    if (error) return toast.error(error.message);
    setDriveFolderSaved(value);
    toast.success("Drive folder saved");
  };

  const handleUpload = async (file: File) => {
    const houseId = houseByBenchmark.get(uploadBM);
    if (!houseId) { toast.error(`No fund house entry for Benchmark ${uploadBM}`); return; }
    setUploading(true);
    setExtracted(null);
    setExtractedMeta(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("fund_house_name", selectedName);
      const { data, error } = await supabase.functions.invoke("factsheet-extract", { body: fd });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const list = (data?.holdings || []) as ExtractedHolding[];
      if (!list.length) { toast.error("No holdings extracted from file"); return; }
      setExtracted(list);
      setExtractedMeta({
        source_filename: data.source_filename || file.name,
        drive_file_id: data.drive_file_id || null,
        drive_file_url: data.drive_file_url || null,
        as_of_date: data.as_of_date || null,
      });
      if (data?.drive_error) toast.warning(`Drive: ${data.drive_error}`);
      else toast.success(`Extracted ${list.length} holdings`);
    } catch (e: any) {
      toast.error(e?.message || "Extraction failed");
    } finally {
      setUploading(false);
    }
  };

  const applyExtracted = async () => {
    if (!extracted) return;
    const houseId = houseByBenchmark.get(uploadBM);
    if (!houseId) { toast.error("Missing fund house id"); return; }
    setApplying(true);
    try {
      const { data, error } = await supabase.functions.invoke("factsheet-apply", {
        body: {
          fund_house_id: houseId,
          holdings: extracted,
          source_filename: extractedMeta?.source_filename,
          drive_file_id: extractedMeta?.drive_file_id,
          drive_file_url: extractedMeta?.drive_file_url,
          as_of_date: extractedMeta?.as_of_date,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Replaced with ${data.inserted} holdings`);
      // A replace can land the holdings but lose the sector mappings. Never let that pass quietly.
      if (data?.mapping_warning) toast.warning(data.mapping_warning, { duration: 15000 });
      setExtracted(null);
      setExtractedMeta(null);
      loadHoldings();
    } catch (e: any) {
      toast.error(e?.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const handleZipUpload = async (zipFile: File) => {
    if (!selectedName) { toast.error("Select a fund house first"); return; }
    setUploading(true);
    try {
      const zip = await JSZip.loadAsync(zipFile);
      const entries: { name: string; file: JSZip.JSZipObject }[] = [];
      zip.forEach((path, entry) => {
        if (entry.dir) return;
        const base = path.split("/").pop() || path;
        if (base.startsWith(".") || base.startsWith("__MACOSX")) return;
        if (!/\.(pdf|csv)$/i.test(base)) return;
        entries.push({ name: base, file: entry });
      });
      if (!entries.length) {
        toast.error("ZIP contains no PDF or CSV files");
        setUploading(false);
        return;
      }

      const items: BatchItem[] = entries.map(e => ({
        filename: e.name,
        benchmark: uploadBM,
        status: "pending",
        holdings: [],
        drive_file_id: null,
        drive_file_url: null,
        as_of_date: null,
        approved: false,
      }));
      setBatchItems(items);
      setBatchProgress({ done: 0, total: entries.length });
      toast.message(`Extracting ${entries.length} files…`);

      // Sequential extraction (avoid AI gateway rate limits)
      for (let i = 0; i < entries.length; i++) {
        setBatchItems(prev => prev ? prev.map((it, j) => j === i ? { ...it, status: "extracting" } : it) : prev);
        try {
          const blob = await entries[i].file.async("blob");
          const isPdf = /\.pdf$/i.test(entries[i].name);
          const innerFile = new File([blob], entries[i].name, { type: isPdf ? "application/pdf" : "text/csv" });
          const fd = new FormData();
          fd.append("file", innerFile);
          fd.append("fund_house_name", selectedName);
          const { data, error } = await supabase.functions.invoke("factsheet-extract", { body: fd });
          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          const list = (data?.holdings || []) as ExtractedHolding[];
          setBatchItems(prev => prev ? prev.map((it, j) => j === i ? {
            ...it,
            status: list.length ? "ready" : "error",
            holdings: list,
            drive_file_id: data?.drive_file_id || null,
            drive_file_url: data?.drive_file_url || null,
            as_of_date: data?.as_of_date || null,
            error: list.length ? null : "No holdings extracted",
            approved: list.length > 0,
          } : it) : prev);
        } catch (e: any) {
          setBatchItems(prev => prev ? prev.map((it, j) => j === i ? {
            ...it,
            status: "error",
            error: e?.message || "Extraction failed",
          } : it) : prev);
        }
        setBatchProgress(p => p ? { ...p, done: i + 1 } : p);
      }
      toast.success("Extraction complete — review and approve");
    } catch (e: any) {
      toast.error(e?.message || "ZIP processing failed");
    } finally {
      setUploading(false);
    }
  };

  const applyBatch = async () => {
    if (!batchItems) return;
    const targets = batchItems
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.approved && it.status === "ready");
    if (!targets.length) { toast.error("Nothing approved to apply"); return; }

    // factsheet-apply does a delete-and-replace per fund house, so two approved files targeting the
    // SAME benchmark would silently overwrite each other (the second wins). Block instead, and mark
    // the conflicting items so the user can deselect the duplicates.
    const byBenchmark = new Map<string, number>();
    for (const { it } of targets) {
      const k = String(it.benchmark);
      byBenchmark.set(k, (byBenchmark.get(k) ?? 0) + 1);
    }
    const dupes = new Set([...byBenchmark.entries()].filter(([, n]) => n > 1).map(([bm]) => bm));
    if (dupes.size > 0) {
      const dupeIdx = new Set(targets.filter(({ it }) => dupes.has(String(it.benchmark))).map(({ i }) => i));
      setBatchItems(prev => prev ? prev.map((x, j) => dupeIdx.has(j)
        ? { ...x, status: "error", error: `Duplicate benchmark BM ${x.benchmark} in this batch — only one file per benchmark per apply` }
        : x) : prev);
      toast.error(`Two or more approved files target the same benchmark (BM ${[...dupes].join(", ")}). Deselect the duplicates and apply again.`);
      return;
    }

    setBatchApplying(true);
    let ok = 0, failed = 0;
    const mappingWarnings: string[] = [];
    for (const { it, i } of targets) {
      const houseId = houseByBenchmark.get(it.benchmark);
      if (!houseId) {
        setBatchItems(prev => prev ? prev.map((x, j) => j === i ? { ...x, status: "error", error: `No fund house entry for BM ${it.benchmark}` } : x) : prev);
        failed++; continue;
      }
      try {
        const { data, error } = await supabase.functions.invoke("factsheet-apply", {
          body: {
            fund_house_id: houseId,
            holdings: it.holdings,
            source_filename: it.filename,
            drive_file_id: it.drive_file_id,
            drive_file_url: it.drive_file_url,
            as_of_date: it.as_of_date,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (data?.mapping_warning) mappingWarnings.push(`${it.filename}: ${data.mapping_warning}`);
        setBatchItems(prev => prev ? prev.map((x, j) => j === i ? { ...x, status: "applied" } : x) : prev);
        ok++;
      } catch (e: any) {
        setBatchItems(prev => prev ? prev.map((x, j) => j === i ? { ...x, status: "error", error: e?.message || "Apply failed" } : x) : prev);
        failed++;
      }
    }
    setBatchApplying(false);
    if (ok) toast.success(`Applied ${ok} factsheet${ok === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    else toast.error("All applies failed");
    if (mappingWarnings.length) {
      toast.warning(
        `Sector mappings were lost on ${mappingWarnings.length} of ${ok + failed} file(s). Re-check this fund house's mappings.`,
        { duration: 20000 },
      );
      console.error(mappingWarnings.join(" | "));
    }
    loadHoldings();
  };

  useEffect(() => { loadHouses().finally(() => setLoading(false)); loadSettings(); loadCompleteness(); }, []);
  useEffect(() => { if (!selectedName && houseNames.length) setSelectedName(houseNames[0]); }, [houseNames, selectedName]);
  useEffect(() => { loadHoldings(); /* eslint-disable-next-line */ }, [selectedName, houses.length]);
  useEffect(() => { if (showHistory) loadSnapshots(); /* eslint-disable-next-line */ }, [showHistory, selectedName, houses.length]);

  const matrix = useMemo(() => {
    const houseToBM = new Map<string, number>();
    houses.forEach(h => houseToBM.set(h.id, h.benchmark));
    // A fund house can hold several rows under ONE name in the same benchmark: Santander's
    // custodian file lists nine to sixteen separate "CURRENCY CONTRACT - GBP" legs per portfolio.
    // Keying by name alone and overwriting would hide all but the last of them, which silently
    // understated the column total and made Delete look like it did nothing (it removed one leg
    // of sixteen). So a cell carries EVERY underlying row id and the NET of their weights.
    const m = new Map<string, Map<number, { ids: string[]; weight: number }>>();
    holdings.forEach(h => {
      const bm = houseToBM.get(h.fund_house_id);
      if (!bm) return;
      if (!m.has(h.holding_name)) m.set(h.holding_name, new Map());
      const row = m.get(h.holding_name)!;
      const cell = row.get(bm);
      if (cell) {
        cell.ids.push(h.id);
        cell.weight += Number(h.holding_weight_pct) || 0;
      } else {
        row.set(bm, { ids: [h.id], weight: Number(h.holding_weight_pct) || 0 });
      }
    });
    return m;
  }, [holdings, houses]);

  const rowNames = useMemo(() => Array.from(matrix.keys()).sort((a, b) => a.localeCompare(b)), [matrix]);

  const totals = useMemo(() => {
    const t: Record<number, number> = {};
    BENCHMARKS.forEach(bm => {
      let sum = 0;
      matrix.forEach(row => { sum += row.get(bm)?.weight || 0; });
      t[bm] = sum;
    });
    return t;
  }, [matrix]);

  // A published book should sum to 100%. Anything outside this band means the source captured
  // the wrong table, lost rows, or double-counted. Houses with no holdings at all are excluded:
  // that is "not loaded yet", not "loaded wrong".
  const COMPLETENESS_MIN = 98;
  const COMPLETENESS_MAX = 102;

  const completenessIssues = useMemo(() => {
    const out: Array<{ id: string; name: string; benchmark: number; sum: number }> = [];
    houses.forEach(h => {
      const sum = bookSums.get(h.id);
      if (sum === undefined) return;
      if (sum < COMPLETENESS_MIN || sum > COMPLETENESS_MAX) {
        out.push({ id: h.id, name: h.name, benchmark: h.benchmark, sum });
      }
    });
    return out.sort((a, b) =>
      Math.abs(b.sum - 100) - Math.abs(a.sum - 100) || a.name.localeCompare(b.name) || a.benchmark - b.benchmark,
    );
  }, [houses, bookSums]);

  const lastUpdatedByBM = useMemo(() => {
    const houseToBM = new Map<string, number>();
    houses.forEach(h => houseToBM.set(h.id, h.benchmark));
    const t: Record<number, string | null> = {};
    holdings.forEach(h => {
      const bm = houseToBM.get(h.fund_house_id);
      if (!bm || !h.updated_at) return;
      const cur = t[bm];
      if (!cur || new Date(h.updated_at) > new Date(cur)) t[bm] = h.updated_at;
    });
    return t;
  }, [holdings, houses]);

  const factsheetAsOfByBM = useMemo(() => {
    const t: Record<number, string | null> = {};
    houses.filter(h => h.name === selectedName).forEach(h => {
      t[h.benchmark] = h.factsheet_as_of || null;
    });
    return t;
  }, [houses, selectedName]);

  const fmtDate = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  const staleByName = useMemo(() => {
    const threshold = Date.now() - 1000 * 60 * 60 * 24 * 90; // ~3 months
    const out: Record<string, { stale: boolean; latest: string | null; missing: boolean }> = {};
    matrix.forEach((row, name) => {
      let latest: string | null = null;
      let anyMapped = false;
      row.forEach(cell => {
        cell.ids.forEach(id => {
          const u = mappingFreshness[id];
          if (u) { anyMapped = true; if (!latest || new Date(u) > new Date(latest)) latest = u; }
        });
      });
      const missing = !anyMapped;
      const stale = missing || (latest !== null && new Date(latest).getTime() < threshold);
      out[name] = { stale, latest, missing };
    });
    return out;
  }, [matrix, mappingFreshness]);

  const updateWeight = async (id: string, value: number) => {
    const { error } = await supabase.from("bm_fund_house_holdings").update({ holding_weight_pct: value }).eq("id", id);
    if (error) return toast.error(error.message);
    await supabase.rpc("bm_recompute_allocations");
    loadHoldings();
  };

  const addHolding = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Holding name required"); return; }
    if (matrix.has(name)) { toast.error("Holding already exists"); return; }
    const ids = Array.from(houseByBenchmark.values());
    if (!ids.length) { toast.error("Select a fund house first"); return; }
    const rows = ids.map(fund_house_id => ({ fund_house_id, holding_name: name, holding_weight_pct: 0, sort_order: 0 }));
    const { error } = await supabase.from("bm_fund_house_holdings").insert(rows);
    if (error) return toast.error(error.message);
    await supabase.rpc("bm_recompute_allocations");
    setNewName("");
    toast.success("Holding added");
    loadHoldings();
  };

  const renameHolding = async (oldName: string, newNm: string) => {
    const trimmed = newNm.trim();
    if (!trimmed || trimmed === oldName) { setEditing(null); return; }
    if (matrix.has(trimmed)) { toast.error("Holding name already exists"); return; }
    const row = matrix.get(oldName);
    if (!row) return;
    const ids = Array.from(row.values()).flatMap(c => c.ids);
    const { error } = await supabase.from("bm_fund_house_holdings").update({ holding_name: trimmed }).in("id", ids);
    if (error) return toast.error(error.message);
    setEditing(null);
    toast.success("Renamed");
    loadHoldings();
  };

  const deleteHolding = async (name: string) => {
    const row = matrix.get(name);
    if (!row) return;
    // One name can cover many rows in the same benchmark, so say how many are actually going.
    const ids = Array.from(row.values()).flatMap(c => c.ids);
    const detail = ids.length > row.size
      ? `Delete all ${ids.length} "${name}" rows across ${row.size} benchmarks?`
      : `Delete "${name}" across all 5 benchmarks?`;
    if (!confirm(detail)) return;
    const { data, error } = await supabase
      .from("bm_fund_house_holdings").delete().in("id", ids).select("id");
    if (error) return toast.error(error.message);
    // PostgREST reports a no-op delete as success, so an RLS block looks identical to a delete
    // that worked. Check what actually came back rather than claiming it did.
    const removed = (data || []).length;
    if (removed === 0) {
      toast.error("Nothing was deleted. You may not have permission to edit benchmark holdings.");
      return;
    }
    await supabase.rpc("bm_recompute_allocations");
    toast.success(removed === 1 ? "Deleted" : `Deleted ${removed} rows`);
    loadHoldings();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Fund House BM Holdings</CardTitle>
            <CardDescription>Weightings of all holdings across the 5 benchmark equivalent funds for the selected fund house.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Fund House:</span>
            <Select value={selectedName} onValueChange={setSelectedName}>
              <SelectTrigger className="w-[280px]"><SelectValue placeholder="Select fund house" /></SelectTrigger>
              <SelectContent>
                {houseNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowHistory(true)} disabled={!selectedName}>
              <History className="h-4 w-4 mr-1" /> History
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {completenessIssues.length > 0 && (
          <div className="p-3 border rounded-md border-amber-500/50 bg-amber-500/10">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="space-y-1.5 min-w-0">
                <p className="text-sm font-medium">
                  {completenessIssues.length} benchmark {completenessIssues.length === 1 ? "book does" : "books do"} not sum to 100%
                </p>
                <p className="text-xs text-muted-foreground">
                  Sector weightings are normalised by each fund house total, so these still show as 100% downstream.
                  A total outside {COMPLETENESS_MIN}% to {COMPLETENESS_MAX}% usually means the import captured the wrong
                  table, dropped negative rows, or double-counted.
                </p>
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {completenessIssues.map(i => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => setSelectedName(i.name)}
                      className={`text-xs px-2 py-0.5 rounded border tabular-nums transition-colors hover:bg-background ${
                        i.name === selectedName ? "border-amber-600 font-medium" : "border-amber-500/40"
                      }`}
                      title={`Show ${i.name}`}
                    >
                      {i.name} BM{i.benchmark}: {i.sum.toFixed(2)}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="New holding name" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-xs" />
          <Button size="sm" onClick={addHolding} disabled={!selectedName}>
            <Plus className="h-4 w-4 mr-1" /> Add Holding
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 p-3 border rounded-md bg-muted/30">
          <span className="text-sm font-medium">Import factsheet:</span>
          <span className="text-xs text-muted-foreground">Benchmark</span>
          <Select value={String(uploadBM)} onValueChange={(v) => setUploadBM(Number(v))}>
            <SelectTrigger className="w-[110px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BENCHMARKS.map(bm => <SelectItem key={bm} value={String(bm)}>BM {bm}</SelectItem>)}
            </SelectContent>
          </Select>
          <input
            id="factsheet-file"
            type="file"
            accept=".pdf,.csv,application/pdf,text/csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!selectedName || uploading}
            onClick={() => document.getElementById("factsheet-file")?.click()}
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? "Extracting…" : "Upload PDF / CSV"}
          </Button>
          <input
            id="factsheet-zip"
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleZipUpload(f); e.target.value = ""; }}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!selectedName || uploading}
            onClick={() => document.getElementById("factsheet-zip")?.click()}
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileArchive className="h-4 w-4 mr-1" />}
            Upload ZIP (batch)
          </Button>
          <span className="text-xs text-muted-foreground">
            Single file applies to selected BM. ZIP lets you assign each file to a benchmark in the review step.
          </span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : rowNames.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No holdings configured for this fund house.</p>
        ) : (
          <div className="overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[260px] sticky left-0 bg-background z-10">Holding</TableHead>
                  <TableHead className="w-[110px]">Actions</TableHead>
                  {BENCHMARKS.map(bm => {
                    const asOf = factsheetAsOfByBM[bm];
                    const ageDays = asOf ? Math.floor((Date.now() - new Date(asOf).getTime()) / 86400000) : null;
                    const isStale = ageDays !== null && ageDays > 35;
                    return (
                      <TableHead key={bm} className="text-right min-w-[120px] align-bottom">
                        <div>Benchmark {bm}</div>
                        <div className="text-[11px] font-normal text-muted-foreground flex items-center justify-end gap-1">
                          <span>{asOf ? `as at ${fmtDate(asOf)}` : "as at —"}</span>
                          {isStale && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  Factsheet is {ageDays} days old (over 5 weeks). Consider re-uploading.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowNames.map(name => {
                  const row = matrix.get(name)!;
                  return (
                    <TableRow key={name}>
                      <TableCell className="font-medium sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2">
                          <span>{name}</span>
                          {staleByName[name]?.stale && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="h-4 w-4 text-amber-500 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {staleByName[name].missing
                                    ? "No CBAM Sector Mapping configured for this holding."
                                    : `Sector mapping last reviewed ${fmtDate(staleByName[name].latest)} (over 3 months ago).`}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ name, value: name })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteHolding(name)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      {BENCHMARKS.map(bm => {
                        const cell = row.get(bm);
                        return (
                          <TableCell key={bm} className="text-right tabular-nums">
                            {!cell ? (
                              <span className="text-muted-foreground">—</span>
                            ) : cell.ids.length > 1 ? (
                              // Several source rows share this name in this benchmark (currency-contract
                              // legs, for example). The figure is their net, which cannot be edited as a
                              // single number, so show it and say how many rows it covers.
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center justify-end gap-1.5 cursor-help">
                                      <span>{cell.weight.toFixed(2)}%</span>
                                      <span className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">
                                        ×{cell.ids.length}
                                      </span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Net of {cell.ids.length} separate rows under this name in Benchmark {bm}.
                                    Edit is disabled because the total cannot be split back across them.
                                    Delete removes all {cell.ids.length}.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <div className="relative">
                                <Input
                                  type="number" step="0.01"
                                  defaultValue={cell.weight}
                                  key={cell.ids[0] + ":" + cell.weight}
                                  className="h-8 text-right tabular-nums pr-6"
                                  onBlur={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v) && v !== cell.weight) updateWeight(cell.ids[0], v);
                                  }}
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                <TableRow className="bg-muted/40 font-medium">
                  <TableCell className="sticky left-0 bg-muted/40 z-10">Column Total</TableCell>
                  <TableCell />
                  {BENCHMARKS.map(bm => (
                    <TableCell key={bm} className={`text-right tabular-nums ${
                      (totals[bm] || 0) === 0 ||
                      ((totals[bm] || 0) >= COMPLETENESS_MIN && (totals[bm] || 0) <= COMPLETENESS_MAX)
                        ? "" : "text-destructive"
                    }`}>
                      {(totals[bm] || 0).toFixed(2)}%
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="bg-muted/20 text-xs text-muted-foreground">
                  <TableCell className="sticky left-0 bg-muted/20 z-10">Last Updated</TableCell>
                  <TableCell />
                  {BENCHMARKS.map(bm => (
                    <TableCell key={bm} className="text-right tabular-nums">
                      {fmtDate(lastUpdatedByBM[bm])}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Holding</DialogTitle>
            <DialogDescription>Update the holding name across all 5 benchmarks.</DialogDescription>
          </DialogHeader>
          {editing && (
            <Input
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") renameHolding(editing.name, editing.value); }}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && renameHolding(editing.name, editing.value)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review extracted holdings */}
      <Dialog open={!!extracted} onOpenChange={(o) => { if (!o) { setExtracted(null); setExtractedMeta(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Extracted Holdings</DialogTitle>
            <DialogDescription>
              {extractedMeta?.source_filename} → {selectedName} (Benchmark {uploadBM}). Edit names/weights, then Apply to snapshot &amp; replace.
              {extractedMeta?.drive_file_url && (
                <> · <a className="underline inline-flex items-center" href={extractedMeta.drive_file_url} target="_blank" rel="noreferrer">Drive file <ExternalLink className="h-3 w-3 ml-1" /></a></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Holding</TableHead>
                  <TableHead className="w-[120px] text-right">Weight %</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(extracted || []).map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Input
                        value={h.holding_name}
                        onChange={(e) => setExtracted(prev => prev!.map((r, j) => j === i ? { ...r, holding_name: e.target.value } : r))}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number" step="0.01"
                        value={h.holding_weight_pct}
                        onChange={(e) => setExtracted(prev => prev!.map((r, j) => j === i ? { ...r, holding_weight_pct: parseFloat(e.target.value) || 0 } : r))}
                        className="h-8 text-right tabular-nums"
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setExtracted(prev => prev!.filter((_, j) => j !== i))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-muted-foreground">
            Total: {(extracted || []).reduce((s, h) => s + (Number(h.holding_weight_pct) || 0), 0).toFixed(2)}% · {extracted?.length || 0} rows
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExtracted(null); setExtractedMeta(null); }}>Cancel</Button>
            <Button onClick={applyExtracted} disabled={applying || !extracted?.length}>
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Snapshot &amp; Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Batch (ZIP) review */}
      <Dialog open={!!batchItems} onOpenChange={(o) => { if (!o && !batchApplying) { setBatchItems(null); setBatchProgress(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Batch Factsheet Review — {selectedName}</DialogTitle>
            <DialogDescription>
              {batchProgress && batchProgress.done < batchProgress.total
                ? `Extracting ${batchProgress.done}/${batchProgress.total}…`
                : `Set the benchmark for each file, untick to skip, then Apply All Approved. Each file snapshots & replaces holdings for the chosen benchmark.`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>File</TableHead>
                  <TableHead className="w-[110px]">Benchmark</TableHead>
                  <TableHead className="w-[90px] text-right">Holdings</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(batchItems || []).map((it, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={it.approved}
                        disabled={it.status !== "ready"}
                        onChange={(e) => setBatchItems(prev => prev ? prev.map((x, j) => j === i ? { ...x, approved: e.target.checked } : x) : prev)}
                      />
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium truncate max-w-[320px]" title={it.filename}>{it.filename}</div>
                      {it.error && <div className="text-xs text-destructive">{it.error}</div>}
                      {it.drive_file_url && (
                        <a className="text-xs underline inline-flex items-center text-muted-foreground" href={it.drive_file_url} target="_blank" rel="noreferrer">
                          Drive <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={String(it.benchmark)}
                        onValueChange={(v) => setBatchItems(prev => prev ? prev.map((x, j) => j === i ? { ...x, benchmark: Number(v) } : x) : prev)}
                      >
                        <SelectTrigger className="h-8 w-[90px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {BENCHMARKS.map(bm => <SelectItem key={bm} value={String(bm)}>BM {bm}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{it.holdings.length || "—"}</TableCell>
                    <TableCell>
                      {it.status === "extracting" && <span className="inline-flex items-center text-xs text-muted-foreground"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Extracting</span>}
                      {it.status === "pending" && <span className="text-xs text-muted-foreground">Queued</span>}
                      {it.status === "ready" && <span className="inline-flex items-center text-xs text-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Ready</span>}
                      {it.status === "applied" && <span className="inline-flex items-center text-xs text-emerald-700 font-medium"><CheckCircle2 className="h-3 w-3 mr-1" />Applied</span>}
                      {it.status === "error" && <span className="inline-flex items-center text-xs text-destructive"><XCircle className="h-3 w-3 mr-1" />Error</span>}
                      {it.status === "skipped" && <span className="text-xs text-muted-foreground">Skipped</span>}
                    </TableCell>
                    <TableCell>
                      {it.status === "ready" && (
                        <Button size="sm" variant="ghost" title="Open detailed review"
                          onClick={() => {
                            setExtracted(it.holdings);
                            setExtractedMeta({ source_filename: it.filename, drive_file_id: it.drive_file_id, drive_file_url: it.drive_file_url, as_of_date: it.as_of_date });
                            setUploadBM(it.benchmark);
                          }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-muted-foreground">
            {(batchItems || []).filter(b => b.approved && b.status === "ready").length} of {batchItems?.length || 0} approved · {(batchItems || []).filter(b => b.status === "applied").length} applied
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBatchItems(null); setBatchProgress(null); }} disabled={batchApplying}>Close</Button>
            <Button onClick={applyBatch} disabled={batchApplying || !(batchItems || []).some(b => b.approved && b.status === "ready")}>
              {batchApplying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Apply All Approved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings: Drive folder */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Factsheet Settings</DialogTitle>
            <DialogDescription>
              Google Drive root folder ID where uploaded factsheets are stored (subfolders are auto-created per Fund House).
              Share the folder with <code className="text-xs">bmdataupload@awm-website-login.iam.gserviceaccount.com</code> as Editor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Drive folder ID</label>
            <Input value={driveFolderId} onChange={(e) => setDriveFolderId(e.target.value)} placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz" />
            <p className="text-xs text-muted-foreground">
              From the Drive URL: <code>drive.google.com/drive/folders/&lt;ID&gt;</code>
              {driveFolderSaved && <> · Currently saved: <code>{driveFolderSaved}</code></>}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Close</Button>
            <Button onClick={saveDriveFolder}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Snapshot History — {selectedName}</DialogTitle>
            <DialogDescription>
              Snapshots are taken before any change (manual edit, delete, or AI replace). Showing latest 50.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto space-y-2">
            {snapshots.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No snapshots yet.</p>
            ) : snapshots.map(s => (
              <details key={s.id} className="border rounded-md p-2">
                <summary className="cursor-pointer text-sm flex items-center justify-between gap-2 flex-wrap">
                  <span>
                    <span className="font-medium">{fmtDate(s.snapshot_at)}</span>{" "}
                    <span className="text-muted-foreground">{new Date(s.snapshot_at).toLocaleTimeString()}</span>{" "}
                    · <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{s.reason}</span>
                    {s.source_filename && <> · {s.source_filename}</>}
                  </span>
                  {s.drive_file_url && (
                    <a className="text-xs underline inline-flex items-center" href={s.drive_file_url} target="_blank" rel="noreferrer">
                      Drive <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </summary>
                <div className="mt-2 text-xs">
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Holding</TableHead><TableHead className="text-right">Weight %</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {(s.holdings || []).map((h, i) => (
                        <TableRow key={i}>
                          <TableCell>{h.holding_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(h.holding_weight_pct).toFixed(2)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </details>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
